import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { MoodboardItem } from '@backend/lib/db/schema/moodboard'

// Undo/redo for the moodboard canvas. Holds full snapshots of the item list (mirrors the
// workspace's nodeHistoryStore). A step is pushed only at *commit* points — intermediate
// gesture state (drag/resize overrides, color preview, text drafts) never reaches here, so
// dragging the color picker records nothing until the user lets go.
//
// Snapshots are applied by the detail page (undo/redo → write items back via tRPC), which
// is why undo()/redo() return the target snapshot rather than mutating any document here.
type State = {
  data: MoodboardItem[][]
  cursor: number

  // Push a new committed state, dropping any redo tail beyond the cursor.
  push: (items: MoodboardItem[]) => void
  // Move the cursor and return the snapshot to apply, or null at the ends.
  undo: () => MoodboardItem[] | null
  redo: () => MoodboardItem[] | null
  canUndo: () => boolean
  canRedo: () => boolean
  // Update the current snapshot in place (no new step). For async external changes like
  // background enrichment that mutate the persisted items without being a user action —
  // keeps the current node in sync so a later undo doesn't revert enrichment.
  syncCurrent: (items: MoodboardItem[]) => void
  // Seed a fresh session (cursor at the given baseline) or clear it on leave.
  reset: (items: MoodboardItem[] | null) => void
}

export const moodboardHistoryStore = create<State>((set, get) => ({
  data: [],
  cursor: -1,

  push: (items) =>
    set((s) => ({
      data: [...s.data.slice(0, s.cursor + 1), items],
      cursor: s.cursor + 1,
    })),

  undo: () => {
    const s = get()
    if (s.cursor <= 0) return null
    const cursor = s.cursor - 1
    set({ cursor })
    return s.data[cursor] ?? null
  },

  redo: () => {
    const s = get()
    if (s.cursor >= s.data.length - 1) return null
    const cursor = s.cursor + 1
    set({ cursor })
    return s.data[cursor] ?? null
  },

  canUndo: () => get().cursor > 0,
  canRedo: () => get().cursor < get().data.length - 1,

  syncCurrent: (items) =>
    set((s) => {
      if (s.cursor < 0) return s
      const data = s.data.slice()
      data[s.cursor] = items
      return { data }
    }),

  reset: (items) => set(items ? { data: [items], cursor: 0 } : { data: [], cursor: -1 }),
}))

export const useMoodboardHistory = () => moodboardHistoryStore(useShallow((s) => s))
